import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';

const regions = [
  { id: 'eu', label: 'EU AI Act' },
  { id: 'us', label: 'US Insurance & SEC' },
  { id: 'nist', label: 'NIST AI RMF · Federal' },
  { id: 'ca', label: 'Canadian AIDA' },
];

const content = {
  eu: {
    header: 'EU AI Act Article 13 — Annex III High-Risk Systems · Obligations: August 2, 2026',
    subtitle: 'What happens when your regulator asks: show me how this AI decision was made.',
    askLabel: 'Regulator asks',
    ask: "Provide documentation demonstrating that your AI system's decision logic, data inputs, and human oversight process for application WO/2026/044821 complies with EU AI Act Article 13 transparency obligations.",
    withoutItems: [
      { text: 'AI system policy document — v3.2 — last updated 6 months ago', warn: 'Static document' },
      { text: 'Server logs export — 847MB unstructured — no decision context', warn: 'No structure' },
      { text: 'Model description PDF — GPT-4o, Azure OpenAI', warn: 'No per-decision evidence' },
    ],
    withoutFooter: 'Average assembly time: 3–5 days. Completeness: unknown.',
    withoutResult: 'INSUFFICIENT TRACEABILITY',
    withoutDesc: 'Article 13 transparency obligation not satisfied. Organization unable to demonstrate decision-level auditability. Corrective action required within 30 days.',
    withLabel: 'Organization exports in 90 seconds',
    withTitle: 'Decision Evidence Record DER-2026-047832',
    withItems: [
      { label: 'Model', value: 'GPT-4o 2025-11', check: 'Version verified' },
      { label: 'Input', value: 'Contract application WO/2026/044821', check: 'Logged and redacted' },
      { label: 'Output', value: 'Category Code G01S 7/48, confidence 0.94', check: 'Recorded' },
      { label: 'Data sources', value: '3/3 verified', check: 'Provenance confirmed' },
      { label: 'EU AI Act Art. 13', value: '', check: 'Satisfied' },
      { label: 'Signature', value: 'sha256:a3f9e2c1…', check: 'Tamper-evident' },
      { label: 'Human reviewer', value: 'CA-4471', check: 'Oversight documented' },
    ],
    withFooter: 'Assembly time: 90 seconds. Completeness: 100%.',
    withResult: 'AUDIT SATISFIED',
    withDesc: 'Article 13 transparency obligation demonstrated. Decision-level evidence provided with cryptographic integrity. No corrective action required.',
    cards: [
      { title: 'Per-decision evidence', desc: 'Every AI invocation generates a structured, signed record at the moment of decision — not assembled retroactively from logs.' },
      { title: 'Cryptographic integrity', desc: 'Each record is signed and immutable. Any tampering is immediately detectable. Evidence is legally defensible.' },
      { title: 'Framework-mapped automatically', desc: 'Every record is automatically mapped to EU AI Act Articles, ISO 42001 controls, and NIST AI RMF functions at creation time.' },
      { title: '90-second export', desc: 'A complete, regulator-ready evidence package is generated in seconds, not days. Audit readiness is continuous, not periodic.' },
    ],
    cta1: { text: 'View a real Decision Evidence Record', to: '/dashboard/decision-ledger/DER-2026-047832' },
    cta2: { text: 'Generate your EU AI Act audit package', to: '/dashboard/assurance' },
  },
  us: {
    header: 'Cyber Insurance Renewal · SEC AI Disclosure Risk · Evidence Requirement',
    subtitle: 'What happens when your insurer asks at renewal: demonstrate that your AI systems have documented governance.',
    askLabel: 'Insurer asks at renewal',
    ask: 'Provide evidence that your organization has documented governance, oversight, and auditability for AI systems that influence material business decisions. Failure to demonstrate may result in exclusion or premium adjustment.',
    withoutItems: [
      { text: 'AI usage policy document — last updated 8 months ago', warn: 'Static document' },
      { text: 'Model vendor contracts — OpenAI, Azure terms of service', warn: 'No governance evidence' },
      { text: 'IT security questionnaire — completed manually, no AI-specific section', warn: 'No per-decision records' },
    ],
    withoutFooter: 'Assembly time: 1–2 weeks. Confidence in completeness: low.',
    withoutResult: 'AI RISK EXCLUSION APPLIED',
    withoutDesc: 'Insufficient AI governance documentation. AI-assisted decision coverage excluded from policy. Estimated premium impact: +35–45% on renewal. Coverage gap on all AI-influenced decisions.',
    withLabel: 'Organization exports in 4 minutes',
    withTitle: 'Insurance Underwriting Package — Apex Assurance Center',
    withItems: [
      { label: 'Evidence completeness', value: '81%', check: 'Demonstrated governance maturity' },
      { label: 'Decision records', value: '47,832 this month', check: 'Continuous auditability' },
      { label: 'Human oversight', value: '', check: 'Agent Controls verified' },
      { label: 'Incident response', value: '', check: 'SOC Decision Records included' },
      { label: 'Framework alignment', value: 'NIST AI RMF', check: 'Self-assessed 71%' },
      { label: 'Model inventory', value: '5 models, 4/5 audited', check: 'Registered' },
    ],
    withFooter: 'Assembly time: 4 minutes. Evidence completeness: 81%.',
    withResult: 'RENEWAL APPROVED',
    withDesc: 'AI governance demonstrated. Continuous decision auditability confirmed. No AI risk exclusion applied. Renewal proceeds at standard terms. Estimated premium saving: $180,000–$340,000 annually.',
    cards: [
      { title: 'Continuous governance proof', desc: 'Apex produces evidence records year-round, not assembled at renewal time. Your insurer receives the same quality of evidence every cycle.' },
      { title: 'Quantifiable risk reduction', desc: 'Documented human oversight, decision auditability, and agent controls translate directly to reduced AI risk exposure in underwriter scoring models.' },
      { title: 'SEC disclosure readiness', desc: 'When AI systems influence material decisions, Apex provides the documentation trail that satisfies SEC cybersecurity disclosure obligations and reduces D&O exposure.' },
      { title: '4-minute package generation', desc: 'Your Insurance Underwriting Package is always current. No scramble at renewal. No manually assembled questionnaires.' },
    ],
    cta1: { text: 'View Insurance Underwriting Package', to: '/dashboard/assurance' },
    cta2: { text: 'See your AI risk score', to: '/dashboard/risk-score' },
  },
  nist: {
    header: 'NIST AI RMF Compliance · Federal Procurement & FedRAMP AI Governance',
    subtitle: 'What happens when a federal agency or procurement officer asks: show me your NIST AI RMF implementation.',
    askLabel: 'Federal procurement officer asks',
    ask: 'Demonstrate operational implementation of NIST AI RMF Govern, Map, Measure, and Manage functions for AI systems used in or connected to federal workflows. Documentation must be current and auditable.',
    withoutItems: [
      { text: 'NIST AI RMF self-assessment document — completed 9 months ago', warn: 'Point-in-time only' },
      { text: 'Risk register spreadsheet — manually maintained', warn: 'Not continuous' },
      { text: 'Vendor AI governance documentation', warn: 'No operational evidence' },
    ],
    withoutFooter: 'Assessment reflects a point in time. No continuous evidence. Renewal requires full reassessment.',
    withoutResult: 'GOVERNANCE GAP IDENTIFIED',
    withoutDesc: 'NIST AI RMF implementation not operationally demonstrated. Point-in-time documentation insufficient for continuous assurance requirement. Procurement approval delayed pending remediation.',
    withLabel: 'Organization demonstrates continuously',
    withTitle: 'NIST AI RMF Operational Evidence — Apex Platform',
    withItems: [
      { label: 'NIST Govern', value: 'Roles & RACI defined', check: 'Roles screen active' },
      { label: 'NIST Map', value: 'AI inventory complete', check: '5 models registered' },
      { label: 'NIST Measure', value: '47,832 decision records', check: 'Continuous measurement' },
      { label: 'NIST Manage', value: 'Exceptions workflow', check: 'Agent Controls active' },
      { label: 'Evidence currency', value: 'Today', check: 'Continuous, not point-in-time' },
    ],
    withFooter: 'Continuous implementation evidence. Always current. No reassessment required.',
    withResult: 'GOVERNANCE DEMONSTRATED',
    withDesc: 'NIST AI RMF operationally implemented across all four functions. Continuous evidence available. Procurement approval granted. FedRAMP AI governance alignment confirmed.',
    cards: [
      { title: 'NIST AI RMF operationalized', desc: 'Most organizations document NIST AI RMF. Apex makes it continuous — every decision record is evidence that the framework is running, not just described.' },
      { title: 'All four functions covered', desc: 'Govern through Roles & RACI. Map through AI Inventory. Measure through the Decision Ledger. Manage through Exceptions and Agent Controls.' },
      { title: 'Continuous not periodic', desc: 'Federal procurement increasingly requires continuous assurance. Apex provides live evidence, not annual assessments.' },
      { title: 'FedRAMP alignment', desc: 'AWS CloudTrail, Microsoft Entra ID, and Azure OpenAI integrations map directly to FedRAMP control families. Evidence coverage: 97%.' },
    ],
    cta1: { text: 'View NIST AI RMF compliance', to: '/dashboard/compliance' },
    cta2: { text: 'Download NIST evidence package', to: '/dashboard/assurance' },
  },
  ca: {
    header: 'Canadian Artificial Intelligence and Data Act (AIDA) · Proposed Legislation · High-Impact System Requirements on Passage',
    subtitle: 'What happens when your regulator or auditor asks for evidence of AIDA-compliant AI governance for a high-impact system.',
    askLabel: 'AIDA regulator asks',
    ask: 'Provide documentation demonstrating that your high-impact AI system has implemented appropriate risk mitigation measures, maintains records of decision-making processes, and has established human oversight mechanisms as required under AIDA.',
    withoutItems: [
      { text: 'Risk assessment document — v1 draft', warn: 'Incomplete' },
      { text: 'AIDA readiness checklist — self-assessed', warn: 'No operational evidence' },
      { text: 'Privacy impact assessment — PIPEDA focused, no AI-specific section', warn: 'Wrong framework' },
    ],
    withoutFooter: 'AIDA-specific documentation largely missing. Significant remediation required.',
    withoutResult: 'GOVERNANCE READINESS GAP',
    withoutDesc: 'Insufficient documentation for high-impact AI system designation. Risk mitigation measures not demonstrably implemented. Governance readiness gap identified ahead of AIDA passage.',
    withLabel: 'Organization demonstrates',
    withTitle: 'AIDA High-Impact System Evidence — Apex Platform',
    withItems: [
      { label: 'System registration', value: 'AI Inventory', check: '5 systems registered' },
      { label: 'Risk mitigation', value: 'Compliance controls', check: '6/7 gaps resolved' },
      { label: 'Decision records', value: '47,832 records', check: 'Human oversight documented' },
      { label: 'Anomaly monitoring', value: 'Evidence Vault', check: 'Continuous integrity checks' },
      { label: 'PIPEDA alignment', value: 'Data Access Records', check: 'All sources logged' },
      { label: 'ISO 42001', value: '55%', check: 'Gap remediation in progress' },
    ],
    withFooter: 'AIDA high-impact system requirements operationally demonstrated.',
    withResult: 'GOVERNANCE READY FOR AIDA PASSAGE',
    withDesc: 'High-impact AI system governance documented and operational. Organization ready for AIDA obligations upon passage. ISO 42001 certification path active.',
    cards: [
      { title: 'AIDA-ready from day one', desc: 'Apex captures every element AIDA requires for high-impact systems: risk documentation, decision records, human oversight evidence, and anomaly monitoring.' },
      { title: 'ISO 42001 acceleration', desc: 'Canadian regulators reference ISO 42001 as the benchmark for AI governance maturity. Apex tracks your certification progress and identifies gaps in real time.' },
      { title: 'PIPEDA + AIDA alignment', desc: 'Data Access Records document exactly which personal data was accessed during each AI decision — satisfying both AIDA and PIPEDA obligations simultaneously.' },
      { title: 'Built for Canadian regulated industries', desc: 'Financial services, telecoms, and federally regulated industries face the earliest AIDA exposure. Apex targets these verticals first.' },
    ],
    cta1: { text: 'View ISO 42001 compliance', to: '/dashboard/compliance' },
    cta2: { text: 'Generate AIDA evidence package', to: '/dashboard/assurance' },
  },
};

function AuditorCard({ label, text }) {
  return (
    <div className="p-4 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
      <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-2 font-semibold">{label}</div>
      <div className="text-xs text-[#94A3B8] leading-relaxed italic">"{text}"</div>
    </div>
  );
}

export default function RegulatoryMomentOfTruth() {
  const [region, setRegion] = useState('eu');
  const c = content[region];

  return (
    <div data-testid="regulatory-moment-of-truth" className="space-y-6 max-w-6xl mx-auto">
      <div className="text-center mb-2">
        <div className="text-[10px] text-[#6366F1] uppercase tracking-[0.2em] font-semibold mb-2">{c.header}</div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#F8FAFC] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {c.subtitle}
        </h1>
      </div>

      {/* Region toggle */}
      <div className="flex items-center justify-center" data-testid="region-toggle">
        <div className="inline-flex bg-[#0F1629] border border-[#1E2A45] rounded-full p-1 gap-1">
          {regions.map((r) => (
            <button
              key={r.id}
              data-testid={`region-${r.id}`}
              onClick={() => setRegion(r.id)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${region === r.id ? 'bg-[#6366F1] text-white shadow-[0_0_12px_rgba(99,102,241,0.3)]' : 'text-[#64748B] hover:text-[#F8FAFC]'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Two-column comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* WITHOUT */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-[#EF4444] uppercase tracking-wider">Without Apex</span>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20">Current state</span>
          </div>
          <div className="rounded-xl border-2 border-[#EF4444]/20 bg-[#EF4444]/[0.02] p-5 space-y-4">
            <AuditorCard label={c.askLabel} text={c.ask} />
            <div>
              <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-2 font-semibold">Organization responds with</div>
              <div className="space-y-2">
                {c.withoutItems.map((item) => (
                  <div key={item.warn} className="flex items-start gap-2 p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
                    <AlertTriangle size={13} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-xs text-[#94A3B8]">{item.text}</div>
                      <div className="text-[10px] text-[#F59E0B] mt-0.5">{item.warn}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-[#F59E0B] mt-2">{c.withoutFooter}</div>
            </div>
            <div className="p-4 rounded-lg bg-[#EF4444]/5 border-2 border-[#EF4444]/30 text-center">
              <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-2 font-semibold">Finding</div>
              <div className="text-lg font-bold text-[#EF4444] mb-1">{c.withoutResult}</div>
              <div className="text-[11px] text-[#94A3B8] leading-relaxed">{c.withoutDesc}</div>
            </div>
          </div>
        </div>
        {/* WITH */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">With Apex</span>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">Apex AI Trust deployed</span>
          </div>
          <div className="rounded-xl border-2 border-[#6366F1]/20 bg-[#6366F1]/[0.02] p-5 space-y-4">
            <AuditorCard label={c.askLabel} text={c.ask} />
            <div>
              <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-2 font-semibold">{c.withLabel}</div>
              <div className="p-3 rounded-lg bg-[#080D1A] border border-[#6366F1]/30">
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={14} className="text-[#6366F1]" />
                  <span className="text-xs text-[#F8FAFC] font-semibold">{c.withTitle}</span>
                </div>
                <div className="space-y-1.5 pl-5">
                  {c.withItems.map((item) => (
                    <div key={item.label} className="flex items-center gap-2 text-[11px]">
                      <CheckCircle2 size={11} className="text-[#10B981] flex-shrink-0" />
                      <span className="text-[#64748B] w-28 flex-shrink-0">{item.label}:</span>
                      {item.value && <span className="text-[#94A3B8] font-mono text-[10px]">{item.value}</span>}
                      <span className="text-[#10B981] text-[10px] ml-auto flex-shrink-0">{item.check}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-[10px] text-[#10B981] mt-2">{c.withFooter}</div>
            </div>
            <div className="p-4 rounded-lg bg-[#10B981]/5 border-2 border-[#10B981]/30 text-center">
              <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-2 font-semibold">Finding</div>
              <div className="text-lg font-bold text-[#10B981] mb-1">{c.withResult}</div>
              <div className="text-[11px] text-[#94A3B8] leading-relaxed">{c.withDesc}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-4">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-4 text-center" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          What Apex captures that logs and policy documents cannot
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {c.cards.map((card) => (
            <div key={card.title} className="glass-card p-4">
              <div className="text-xs font-semibold text-[#6366F1] mb-2">{card.title}</div>
              <div className="text-[11px] text-[#64748B] leading-relaxed">{card.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card p-4 flex items-center justify-between flex-wrap gap-3">
        <Link to={c.cta1.to} data-testid="cta-left" className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1 font-medium">
          {c.cta1.text} <ArrowRight size={12} />
        </Link>
        <Link to={c.cta2.to} data-testid="cta-right" className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
          {c.cta2.text} <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
